import React, { useState, FormEvent } from 'react';
import { serverAPI, CreateServerPayload, UpdateServerPayload } from '../utils/api';
import { ServerRecord } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../config/supabase';
import ImageUploader from './ImageUploader';
import BannerCropper from './BannerCropper';

interface ServerFormProps {
  server?: ServerRecord;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ServerForm: React.FC<ServerFormProps> = ({ server, onSuccess, onCancel }) => {
  const { t } = useLanguage();
  const isEditing = !!server;

  const [formData, setFormData] = useState({
    name: server?.name || '',
    description: server?.description || '',
    shortDescription: server?.shortDescription || '',
    game: server?.game || 'Metin2',
    region: server?.region || 'EU',
    ipAddress: server?.ipAddress || '',
    websiteUrl: server?.websiteUrl || '',
    downloadUrl: server?.downloadUrl || '',
    discordLink: server?.discordLink || '',
    tags: server?.tags?.join(', ') || '',
    status: server?.status || 'online' as 'online' | 'offline' | 'maintenance' | 'starting_soon',
    bannerUrl: server?.bannerUrl || '',
    bossContentTitle: server?.bossContentTitle || '',
    itemContentTitle: server?.itemContentTitle || '',
    onlineRating: server?.onlineRating || 0,
    managementRating: server?.managementRating || 0,
    gameplayRating: server?.gameplayRating || 0,
  });

  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);
  const [bossContentFiles, setBossContentFiles] = useState<File[]>([]);
  const [itemTransformationFiles, setItemTransformationFiles] = useState<File[]>([]);
  // Filter out empty partners on load
  const [partners, setPartners] = useState<{ name: string; url: string; iconUrl: string }[]>(
    (server?.partners || []).filter(p => p.name && p.name.trim() !== '')
  );
  const [deletedScreenshots, setDeletedScreenshots] = useState<string[]>([]);
  const [deletedBossContent, setDeletedBossContent] = useState<string[]>([]);
  const [deletedItemTransformations, setDeletedItemTransformations] = useState<string[]>([]);

  // New partner form state
  const [newPartner, setNewPartner] = useState({ name: '', url: '', iconUrl: '' });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [bannerToCrop, setBannerToCrop] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create object URL for cropper
    const objectUrl = URL.createObjectURL(file);
    setBannerToCrop(objectUrl);
    setShowCropper(true);

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setShowCropper(false);
    setIsUploading(true);
    setError('');

    try {
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.jpg`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(filePath, croppedBlob, {
          contentType: 'image/jpeg'
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, bannerUrl: publicUrl }));
    } catch (err) {
      console.error('Error uploading banner:', err);
      setError(t('admin.server_form.error_upload'));
    } finally {
      setIsUploading(false);
      if (bannerToCrop) {
        URL.revokeObjectURL(bannerToCrop);
        setBannerToCrop(null);
      }
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (bannerToCrop) {
      URL.revokeObjectURL(bannerToCrop);
      setBannerToCrop(null);
    }
  };

  const uploadFiles = async (files: File[], bucket: string) => {
    const uploaded: { id: string; url: string; sortOrder: number }[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

      uploaded.push({
        id: crypto.randomUUID(),
        url: publicUrl,
        sortOrder: i
      });
    }
    return uploaded;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Upload images
      const uploadedScreenshots = await uploadFiles(screenshotFiles, 'server-screenshots');
      const uploadedBossContent = await uploadFiles(bossContentFiles, 'server-screenshots');
      const uploadedItemTransformations = await uploadFiles(itemTransformationFiles, 'server-screenshots');

      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Merge existing images with newly uploaded ones, filtering out deleted ones
      const existingScreenshots = (server?.screenshots || [])
        .filter(s => !deletedScreenshots.includes(s.url))
        .map(s => ({ id: s.id, url: s.url, sortOrder: s.sortOrder }));
      const existingBossContent = (server?.bossContent || [])
        .filter(s => !deletedBossContent.includes(s.url))
        .map(s => ({ id: s.id, url: s.url, sortOrder: s.sortOrder }));
      const existingItemTransformations = (server?.itemTransformations || [])
        .filter(s => !deletedItemTransformations.includes(s.url))
        .map(s => ({ id: s.id, url: s.url, sortOrder: s.sortOrder }));

      const allScreenshots = [...existingScreenshots, ...uploadedScreenshots];
      const allBossContent = [...existingBossContent, ...uploadedBossContent];
      const allItemTransformations = [...existingItemTransformations, ...uploadedItemTransformations];

      // Filter out partners with empty name or url
      const validPartners = partners.filter(p => p.name.trim() !== '' && p.url.trim() !== '');

      const payload = {
        name: formData.name,
        description: formData.description,
        shortDescription: formData.shortDescription || undefined,
        game: formData.game || undefined,
        region: formData.region || undefined,
        ipAddress: formData.ipAddress || undefined,
        websiteUrl: formData.websiteUrl || undefined,
        downloadUrl: formData.downloadUrl || undefined,
        discordLink: formData.discordLink || undefined,
        tags,
        status: formData.status,
        screenshots: allScreenshots.length > 0 ? allScreenshots : undefined,
        bossContent: allBossContent.length > 0 ? allBossContent : undefined,
        bossContentTitle: formData.bossContentTitle || undefined,
        itemTransformations: allItemTransformations.length > 0 ? allItemTransformations : undefined,
        itemContentTitle: formData.itemContentTitle || undefined,
        partners: validPartners.length > 0 ? validPartners : undefined,
        bannerUrl: formData.bannerUrl || undefined,
        onlineRating: formData.onlineRating > 0 ? formData.onlineRating : undefined,
        managementRating: formData.managementRating > 0 ? formData.managementRating : undefined,
        gameplayRating: formData.gameplayRating > 0 ? formData.gameplayRating : undefined,
      };

      console.log('[ServerForm] Submitting payload:', JSON.stringify(payload, null, 2));

      if (isEditing && server) {
        const result = await serverAPI.updateServer(server.id, payload as UpdateServerPayload);
        alert('Sunucu başarıyla güncellendi! / Server updated successfully!');
      } else {
        const result = await serverAPI.createServer(payload as CreateServerPayload);
        alert('Sunucu başarıyla oluşturuldu! / Server created successfully!');
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Failed to save server:', err);
      const message = (err as Error & { details?: { message?: string } })?.details?.message;
      setError(message || 'Failed to save server. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Banner Cropper Modal */}
      {showCropper && bannerToCrop && (
        <BannerCropper
          imageSrc={bannerToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
            {isEditing ? t('admin.server_form.edit_title') : t('admin.server_form.title')}
          </h2>

          {error && (
            <div className="mb-4 p-4 bg-red-100 dark:bg-red-500/20 border border-red-300 dark:border-red-500 rounded text-red-700 dark:text-red-200">
              {error}
            </div>
          )}

          {/* Server Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.server_name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="Epic PVP Server"
            />
          </div>

          {/* Short Description */}
          <div className="mb-4">
            <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.short_desc')}
            </label>
            <input
              type="text"
              id="shortDescription"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              maxLength={100}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder={t('admin.server_form.short_desc_hint')}
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.full_desc')} *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder={t('admin.server_form.full_desc_hint')}
            />
          </div>

          {/* Game and Region */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="game" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.server_form.game')}
              </label>
              <input
                type="text"
                id="game"
                name="game"
                value={formData.game}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
                placeholder="Metin2"
              />
            </div>

            <div>
              <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.server_form.region')}
              </label>
              <input
                type="text"
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
                placeholder="EU, NA, TR..."
              />
            </div>

            {/* Launch Date - Read-only for editing, shows creation date */}
            {isEditing && server?.createdAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.server_form.launch_date')}
                </label>
                <input
                  type="text"
                  value={new Date(server.createdAt).toLocaleDateString()}
                  disabled
                  className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('admin.server_form.launch_date_readonly') || 'Sunucu oluşturulma tarihi değiştirilemez'}
                </p>
              </div>
            )}
          </div>

          {/* IP Address */}
          <div className="mb-4">
            <label htmlFor="ipAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.ip_address')}
            </label>
            <input
              type="text"
              id="ipAddress"
              name="ipAddress"
              value={formData.ipAddress}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder={t('admin.server_form.ip_address_hint')}
            />
          </div>

          {/* Website URL */}
          <div className="mb-4">
            <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.website_url')}
            </label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="https://example.com"
            />
          </div>

          {/* Download URL */}
          <div className="mb-4">
            <label htmlFor="downloadUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.download_url')}
            </label>
            <input
              type="url"
              id="downloadUrl"
              name="downloadUrl"
              value={formData.downloadUrl}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="https://example.com/download"
            />
          </div>

          {/* Discord Link */}
          <div className="mb-4">
            <label htmlFor="discordLink" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.discord_link') || 'Discord Link'}
            </label>
            <input
              type="url"
              id="discordLink"
              name="discordLink"
              value={formData.discordLink}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="https://discord.gg/yourserver"
            />
          </div>

          {/* Banner Image Upload */}
          <div className="mb-4">
            <label htmlFor="banner" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.banner_image')}
            </label>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  id="banner"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  disabled={isUploading}
                  className="block w-full text-sm text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-purple-500/20 file:text-purple-400
                  hover:file:bg-purple-500/30
                  cursor-pointer"
                />
                {isUploading && <span className="text-gray-400 animate-pulse">{t('admin.server_form.uploading')}</span>}
              </div>

              {formData.bannerUrl && (
                <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 group">
                  <img
                    src={formData.bannerUrl}
                    alt="Server Banner Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <label
                      htmlFor="banner"
                      className="bg-blue-500/80 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {t('admin.server_form.crop_image') || 'Change Banner'}
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, bannerUrl: '' }))}
                      className="bg-red-500/80 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {t('common.delete') || 'Delete'}
                    </button>
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.server_form.banner_hint')}</p>
            </div>
          </div>

          {/* Tags */}
          <div className="mb-4">
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.tags')}
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
              placeholder="pvp, new, turkish, high-rate"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('admin.server_form.tags_hint')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {['PvP', 'PvE', 'High Rate', 'Mid Rate', 'Low Rate', 'Turkish', 'International', '1-99', '1-105', '1-120', 'Orta-Emek', 'Hard', 'Wslik', 'Zor-Emek', 'Simya Kuşak Yok', 'Simya Kuşak Var'].map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    const currentTags = formData.tags ? formData.tags.split(',').map(t => t.trim()) : [];
                    if (!currentTags.includes(tag)) {
                      setFormData(prev => ({ ...prev, tags: [...currentTags, tag].join(', ') }));
                    }
                  }}
                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded text-xs text-gray-700 dark:text-gray-300 transition-colors"
                >
                  + {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="mb-4">
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.server_form.status')} *
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white"
            >
              <option value="online">{t('server.status.online')}</option>
              <option value="offline">{t('server.status.offline')}</option>
              <option value="maintenance">{t('server.status.maintenance')}</option>
              <option value="starting_soon">{t('server.status.starting_soon') || 'Starting Soon'}</option>
            </select>
          </div>

          {/* Category Ratings */}
          <div className="mb-4 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Kategori Derecelendirmeleri
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Online Rating */}
              <div>
                <label htmlFor="onlineRating" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Online
                </label>
                <select
                  id="onlineRating"
                  name="onlineRating"
                  value={formData.onlineRating}
                  onChange={(e) => setFormData(prev => ({ ...prev, onlineRating: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
                >
                  <option value={0}>Seçilmedi</option>
                  <option value={1}>⭐ 1 Yıldız</option>
                  <option value={2}>⭐⭐ 2 Yıldız</option>
                  <option value={3}>⭐⭐⭐ 3 Yıldız</option>
                  <option value={4}>⭐⭐⭐⭐ 4 Yıldız</option>
                  <option value={5}>⭐⭐⭐⭐⭐ 5 Yıldız</option>
                </select>
              </div>

              {/* Management Rating */}
              <div>
                <label htmlFor="managementRating" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Yönetim
                </label>
                <select
                  id="managementRating"
                  name="managementRating"
                  value={formData.managementRating}
                  onChange={(e) => setFormData(prev => ({ ...prev, managementRating: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
                >
                  <option value={0}>Seçilmedi</option>
                  <option value={1}>⭐ 1 Yıldız</option>
                  <option value={2}>⭐⭐ 2 Yıldız</option>
                  <option value={3}>⭐⭐⭐ 3 Yıldız</option>
                  <option value={4}>⭐⭐⭐⭐ 4 Yıldız</option>
                  <option value={5}>⭐⭐⭐⭐⭐ 5 Yıldız</option>
                </select>
              </div>

              {/* Gameplay Rating */}
              <div>
                <label htmlFor="gameplayRating" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Oyun Yapısı
                </label>
                <select
                  id="gameplayRating"
                  name="gameplayRating"
                  value={formData.gameplayRating}
                  onChange={(e) => setFormData(prev => ({ ...prev, gameplayRating: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
                >
                  <option value={0}>Seçilmedi</option>
                  <option value={1}>⭐ 1 Yıldız</option>
                  <option value={2}>⭐⭐ 2 Yıldız</option>
                  <option value={3}>⭐⭐⭐ 3 Yıldız</option>
                  <option value={4}>⭐⭐⭐⭐ 4 Yıldız</option>
                  <option value={5}>⭐⭐⭐⭐⭐ 5 Yıldız</option>
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Server hakkında kategori bazlı derecelendirme yapın.
            </p>
          </div>

          {/* Screenshots Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('server.screenshots')}
            </label>
            <ImageUploader
              existingImages={server?.screenshots?.map(s => ({ id: s.id, url: s.url })) || []}
              onFilesChange={setScreenshotFiles}
              onDeleteExisting={(url) => {
                setDeletedScreenshots(prev => [...prev, url]);
              }}
              maxImages={10}
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('server.screenshots_help')}</p>
          </div>

          {/* Boss Content Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('server.boss_content') || 'Boss Content'}
            </label>
            <input
              type="text"
              name="bossContentTitle"
              value={formData.bossContentTitle}
              onChange={handleChange}
              placeholder={t('admin.server_form.boss_content_title_placeholder') || 'Özel başlık (örn: Etkinlikler, Sistemler)'}
              className="w-full px-4 py-2 mb-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
            />
            <ImageUploader
              existingImages={(server?.bossContent || []).filter(s => !deletedBossContent.includes(s.url)).map(s => ({ id: s.id, url: s.url }))}
              onFilesChange={setBossContentFiles}
              onDeleteExisting={(url) => setDeletedBossContent(prev => [...prev, url])}
              maxImages={10}
            />
          </div>

          {/* Item Transformations Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('server.item_transformations') || 'Item Transformations'}
            </label>
            <input
              type="text"
              name="itemContentTitle"
              value={formData.itemContentTitle}
              onChange={handleChange}
              placeholder={t('admin.server_form.item_content_title_placeholder') || 'Özel başlık (örn: Dönüşümler, İçerikler)'}
              className="w-full px-4 py-2 mb-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
            />
            <ImageUploader
              existingImages={(server?.itemTransformations || []).filter(s => !deletedItemTransformations.includes(s.url)).map(s => ({ id: s.id, url: s.url }))}
              onFilesChange={setItemTransformationFiles}
              onDeleteExisting={(url) => setDeletedItemTransformations(prev => [...prev, url])}
              maxImages={10}
            />
          </div>

          {/* Partners / Yayıncılar ve Reklamcılar Section */}
          <div className="mb-4 bg-gray-100 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-300 dark:border-gray-600">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('server.details.publishers_advertisers') || 'Yayıncılar ve Reklamcılar'}
            </label>

            {/* Existing Partners List - only show non-empty partners */}
            {partners.filter(p => p.name && p.name.trim() !== '').length > 0 && (
              <div className="space-y-3 mb-4">
                {partners.filter(p => p.name && p.name.trim() !== '').map((partner, index) => (
                  <div key={index} className="flex items-center gap-3 bg-gray-200 dark:bg-gray-700/50 p-3 rounded-lg">
                    {partner.iconUrl && partner.iconUrl.trim() !== '' && (
                      <img src={partner.iconUrl} alt={partner.name} className="w-10 h-10 rounded object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 dark:text-white font-medium truncate">{partner.name}</p>
                      <p className="text-gray-500 dark:text-gray-400 text-sm truncate">{partner.url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const originalIndex = partners.findIndex(p => p.name === partner.name && p.url === partner.url);
                        setPartners(prev => prev.filter((_, i) => i !== originalIndex));
                      }}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                      title={t('common.delete') || 'Delete'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Partner Form */}
            <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t('server.edit.add_partner') || 'Yeni Yayıncı/Reklamcı Ekle'}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newPartner.name}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('server.edit.partner_name') || 'İsim'}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                />
                <input
                  type="url"
                  value={newPartner.url}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, url: e.target.value }))}
                  placeholder={t('server.edit.partner_url') || 'Web sitesi URL'}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                />
                <input
                  type="url"
                  value={newPartner.iconUrl}
                  onChange={(e) => setNewPartner(prev => ({ ...prev, iconUrl: e.target.value }))}
                  placeholder={t('server.edit.partner_icon') || 'Logo URL'}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-900 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (newPartner.name.trim() && newPartner.url.trim()) {
                    setPartners(prev => [...prev, { ...newPartner }]);
                    setNewPartner({ name: '', url: '', iconUrl: '' });
                  } else {
                    alert('İsim ve URL alanları zorunludur!');
                  }
                }}
                className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('server.edit.add_partner_btn') || 'Ekle'}
              </button>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {isSubmitting ? t('admin.server_form.creating') : isEditing ? t('admin.server_form.update_server') : t('admin.server_form.create_server')}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 rounded-lg font-semibold transition-colors text-gray-700 dark:text-white"
              >
                {t('admin.server_form.cancel')}
              </button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default ServerForm;
